import type {
  AnalysisChatEvent,
  ToolProgressMessage,
} from "@/backend/analysis-chat-v2/types";
import type { QueryClient } from "@tanstack/react-query";

/**
 * Applies the default React Query side effects for server-driven cache sync
 * events emitted on the chat stream. Intentionally narrow: only handles
 * `invalidate-queries` and `invalidate-feature-table`. Every other event type
 * is a no-op so callers can layer their own side effects above this.
 */
export const applyDefaultStreamSideEffects = ({
  event,
  queryClient,
}: {
  event: AnalysisChatEvent;
  queryClient: QueryClient;
}) => {
  switch (event.type) {
    case "invalidate-queries": {
      for (const queryKey of event.queryKeys) {
        // Also refetch unmounted queries: a report editor mounts once from the cache and does not pick up a later refetch.
        void queryClient.invalidateQueries({ queryKey, refetchType: "all" });
      }
      break;
    }
    case "invalidate-feature-table": {
      // Column payloads replaced the feature table as the per-run column cache.
      void queryClient.invalidateQueries({
        queryKey: ["column"],
        exact: false,
      });
      break;
    }
    case "init":
    case "iteration-start":
    case "tool-progress":
    case "debug":
    case "report-item":
    case "report-complete":
    case "error":
    case "finish":
    case "navigate":
    case "agent-questions":
      break;
  }
};

/**
 * Type guard narrowing an `AnalysisChatEvent` to a `tool-progress` event.
 * `toolName` is required on `ToolProgressMessage`, so a single type check suffices.
 */
export const isToolProgressEvent = (
  event: AnalysisChatEvent
): event is ToolProgressMessage => event.type === "tool-progress";
